// Libraries
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { PictureAsPdf, BorderColor } from '@mui/icons-material';
import { isNotNil } from 'ramda';

// MUI
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';

// Core
import { ResourceTextApplication, paramRepository } from '../../../core/resources';
import { dayFormat } from '../../../core/constants';
import authorization from '../../../core/authorization';
import utils from '../../../core/utils';

// Interfaces
import { AnelmaContractStatusEnum, AnimalTypeEnum } from '../../../interfaces/enums';
import { IContractListItem } from '../../../interfaces/IContract';

// Common
import ViewLoader from '../../../common/ViewLoader';
import { ADataGrid, ADataGridActions, AGridColumns, gridColumns } from '../../../common/dataGrid';
import { ADefaultIconButton, AEditIconButton, ASearchIconButton } from '../../../common/buttons';

export interface ContractListProps {
    contracts: IContractListItem[];
    openContract: (contractNumber: string, contractTitle: string, terminate: boolean) => void;
    downloadContract: (contractNumber: string, ContractTitle: string) => void;
    signContract: (contractNumber: string) => void;
    terminateContract: (contract: IContractListItem) => void;
    showExpired: boolean;
    producerView?: boolean | undefined;
}

interface IContractHistoryRow {
    signedProducer: string;
    producerSignDate: Date | null;
    signedSnellman: string;
    snellmanSignDate: Date | null;
    terminator: string;
    terminationDate: Date | null;
}

export default function ContractsList(props: ContractListProps) {
    const apiRef = useGridApiRef();
    const { contracts, openContract, downloadContract, signContract, terminateContract } = props;

    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaContract']);
    const bovineProductionLinesParams = paramRepository.resource(
        'AnelmaCompany',
        'FarmingTypeBovine'
    );
    const contractStateParams = paramRepository.resource('AnelmaContract', 'State');
    const contractAnimalTypeParams = paramRepository.resource('AnelmaContract', 'Species');
    const contractContractTypeParams = paramRepository.resource('AnelmaContract', 'ContractType');
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        paramRepository.load(['AnelmaCompany', 'AnelmaContract']).then(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (isNotNil(apiRef?.current?.setExpandedDetailPanels))
            apiRef.current.setExpandedDetailPanels([]);
    }, [contracts]);

    const getAnimalType = (animalType: AnimalTypeEnum) => {
        return (
            contractAnimalTypeParams.find((i) => {
                let code = Number(i.code);
                return code === animalType;
            })?.text || `${animalType}`
        );
    };

    type NoticePeriod = 1 | 2 | 3;

    const getNoticeMonths = (notice: NoticePeriod, terminatedBySnellman: boolean): number => {
        switch (notice) {
            case 1:
                return 6;
            case 2:
                return 12;
            case 3:
                return terminatedBySnellman ? 12 : 6;
            default:
                return terminatedBySnellman ? 12 : 6;
        }
    };

    const getNoticePeriod = (c: IContractListItem): NoticePeriod => (c as any).NoticePeriod ?? 3;

    const getContractType = (code: string | number): string => {
        const param = contractContractTypeParams.find((i) => String(i.code) === String(code));
        return param ? param.text : String(code);
    };

    const columns: AGridColumns = [
        gridColumns.withTooltip({
            field: 'Producer',
            headerName: t('AnelmaContract:1003'),

            width: 200,
            valueGetter: (params, row) => {
                return (
                    (row as IContractListItem).Company.ProducerNumber +
                    ' ' +
                    (row as IContractListItem).Company.Name
                );
            },
        }),

        gridColumns.withTooltip({
            field: 'Type',
            headerName: t('AnelmaContract:1005'),

            width: 120,
            valueGetter: (params, row) => {
                return getContractType((row as IContractListItem).Type);
            },
        }),

        gridColumns.withTooltip({
            field: 'Status',
            headerName: t('AnelmaContract:1007'),

            width: 200,
            valueGetter: (params, row) => {
                return (
                    contractStateParams.find((i) => {
                        let code = Number(i.code);
                        let statusCode = (row as IContractListItem).Status as number;

                        return code === statusCode;
                    })?.text || `${(row as IContractListItem).Status as number}`
                );
            },
        }),

        gridColumns.dateTime('EffectiveFrom', t('AnelmaContract:1017'), dayFormat, 110),
        gridColumns.dateTime('EffectiveToDisplay', t('AnelmaContract:1018'), dayFormat, 110),

        gridColumns.withTooltip({
            field: 'SnellmanSignatory',
            headerName: t('AnelmaContract:1042'),

            width: 120,
            valueGetter: (params, row) =>
                getSnellmanContactPersonFromContract(row as IContractListItem),
        }),

        gridColumns.withTooltip({
            field: 'ProductionLines',
            headerName: t('AnelmaContract:1043'),

            width: 200,
            valueGetter: (params, row) => {
                if ((row as IContractListItem).Company.ProductionLines) {
                    var i = (row as IContractListItem).Company.ProductionLines.findIndex(
                        (l) => l.Type === AnimalTypeEnum.Bovine
                    );

                    if (i != -1) {
                        return (row as IContractListItem).Company.ProductionLines[
                            i
                        ].FarmingTypes.map((t) =>
                            getBovineProductionLineName(t.toString().padStart(2, '0'))
                        ).join(', ');
                    }
                }

                return '';
            },
        }),

        gridColumns.withTooltip({
            field: 'AnimalType',
            headerName: t('AnelmaContract:1004'),

            width: 100,
            valueGetter: (params, row) => {
                return getAnimalType((row as IContractListItem).AnimalType);
            },
        }),

        gridColumns.withTooltip({
            field: 'ContractNumber',
            headerName: t('AnelmaContract:1006'),

            width: 200,
            valueGetter: (params, row) => {
                return (row as IContractListItem).Id;
            },
        }),
    ];

    const isContractSigningBtnDisabled = (): boolean => {
        if (authorization.hasAdminContractUpdateAccess) {
            return false;
        }
        return true;
    };

    const getSignBtnTooltipText = (): string => {
        if (isContractSigningBtnDisabled()) {
            return t('AnelmaContract:1052');
        }
        return t('AnelmaContract:1050');
    };

    const getTerminateBtnTooltipText = (): string => {
        if (isContractSigningBtnDisabled()) {
            return t('AnelmaContract:1053');
        }
        return t('AnelmaContract:1051');
    };

    const getActions: () => ADataGridActions = () => {
        return [
            (params) => {
                const contractNumber = (params.row as IContractListItem).Id;
                const rowNumber = (params.row as { id: number }).id;
                const contractTitle = (params.row as IContractListItem).Title;

                return (
                    <ASearchIconButton
                        key={`${rowNumber}-search-btn`}
                        onClick={() => openContract(contractNumber, contractTitle, false)}
                        disabled={false}
                        tooltip={t('AnelmaContract:1054')}
                    />
                );
            },
            (params) => {
                const contractNumber = (params.row as IContractListItem).Id;
                const rowNumber = (params.row as { id: number }).id;
                const contractTitle = (params.row as IContractListItem).Title;

                return (
                    <ADefaultIconButton
                        key={`${rowNumber}-print-btn`}
                        icon={<PictureAsPdf />}
                        onClick={() => downloadContract(contractNumber, contractTitle)}
                        tooltip={t('AnelmaContract:1055')}
                        disabled={false}
                    />
                );
            },
            (params) => {
                const contractNumber = (params.row as IContractListItem).Id;
                const rowNumber = (params.row as { id: number }).id;

                return canStartSigning(contractNumber) ? (
                    <ADefaultIconButton
                        key={`${rowNumber}-start-signing-btn`}
                        icon={<BorderColor />}
                        onClick={() => signContract(contractNumber)}
                        tooltip={getSignBtnTooltipText()}
                        style={{ color: 'orange' }}
                        disabled={isContractSigningBtnDisabled()}
                    />
                ) : (
                    <></>
                );
            },
            (params) => {
                const contractNumber = (params.row as IContractListItem).Id;
                const rowNumber = (params.row as { id: number }).id;

                return canTerminate(contractNumber) ? (
                    <AEditIconButton
                        key={`${rowNumber}-start-signing-btn`}
                        onClick={() => terminateContract(params.row as IContractListItem)}
                        tooltip={getTerminateBtnTooltipText()}
                        style={{ color: 'green' }}
                        disabled={isContractSigningBtnDisabled()}
                    />
                ) : (
                    <></>
                );
            },
        ];
    };

    const canStartSigning = (contractNumber: string) => {
        if (props.producerView || props.showExpired) return false;

        let i = contracts.findIndex((c) => c.Id === contractNumber);

        if (i != -1) {
            let contract = contracts[i];
            return checkCanStartSigning(contract);
        }

        return false;
    };

    const canTerminate = (contractNumber: string) => {
        if (props.producerView || props.showExpired) return false;

        let i = contracts.findIndex((c) => c.Id === contractNumber);

        if (i != -1) {
            let contract = contracts[i];
            return contract.TerminationDate === null && checkCanTerminate(contract);
        }

        return false;
    };

    const checkCanStartSigning = (contract: IContractListItem) => {
        return contract.Status === AnelmaContractStatusEnum.Draft;
    };

    const checkCanTerminate = (contract: IContractListItem) => {
        return contract.Status === AnelmaContractStatusEnum.Signed;
    };

    const getSnellmanContactPersonFromContract = (contract: IContractListItem) => {
        let i = contract.Signatories.findIndex((s) => s.Signer == 2); // 2 = Snellman

        if (i != -1) return contract.Signatories[i].Username;

        return '';
    };

    function getBovineProductionLineName(code: number | string | null): string {
        if (!code) return '';
        return bovineProductionLinesParams.find((i) => i.code === `${code}`)?.text || `${code}`;
    }

    const fmt = (d: Date | string | null | undefined) =>
        d ? (moment(d).isValid() ? moment(d).format('DD.MM.YYYY') : '') : '';

    const getDetailedPanelRows = (contract: IContractListItem) => {
        const producerSigns = contract.Signatories.filter((s) => s.Signer === 1);
        const snellmanSigns = contract.Signatories.filter((s) => s.Signer === 2);

        const max = Math.max(producerSigns.length, snellmanSigns.length);
        const rows: IContractHistoryRow[] = Array.from({ length: max }).map((_, i) => ({
            signedProducer: producerSigns[i]?.Username ?? '',
            producerSignDate: producerSigns[i]?.SigningDate ?? null,
            signedSnellman: snellmanSigns[i]?.Username ?? '',
            snellmanSignDate: snellmanSigns[i]?.SigningDate ?? null,
            terminator: '',
            terminationDate: null,
        }));

        if (contract.TerminationDate) {
            const bySnellman = !!contract.TerminatedBySnellman;

            let termIdx = -1;
            if (bySnellman) {
                termIdx = rows.findIndex((r) => r.signedSnellman);
            } else {
                termIdx = rows.findIndex((r) => r.signedProducer);
            }
            if (termIdx === -1) termIdx = Math.max(0, rows.length - 1);

            rows[termIdx] = {
                ...rows[termIdx],
                terminator: bySnellman
                    ? snellmanSigns[0]?.Username ?? ''
                    : producerSigns[0]?.Username ?? '',
                terminationDate: contract.TerminationDate,
            };
        }

        return rows.map((r, idx) => ({ id: `${contract.Id}-${idx}`, ...r }));
    };

    const detailedPanelColumns: AGridColumns = [
        gridColumns.withTooltip({
            field: 'signedProducer',
            headerName: t('AnelmaContract:1010'),
            width: 200,
            sortable: false,
        }),
        gridColumns.withTooltip({
            field: 'producerSignDate',
            headerName: t('AnelmaContract:1011'),
            width: 110,
            sortable: false,
            valueGetter: (params, row) => {
                const d = (row as IContractHistoryRow).producerSignDate;
                return fmt(d);
            },
        }),
        gridColumns.withTooltip({
            field: 'signedSnellman',
            headerName: t('AnelmaContract:1012'),
            width: 200,
            sortable: false,
        }),
        gridColumns.withTooltip({
            field: 'snellmanSignDate',
            headerName: t('AnelmaContract:1011'),
            width: 110,
            sortable: false,
            valueGetter: (params, row) => {
                const d = (row as IContractHistoryRow).snellmanSignDate;
                return fmt(d);
            },
        }),
        gridColumns.withTooltip({
            field: 'terminator',
            headerName: t('AnelmaContract:1013'),
            width: 200,
            sortable: false,
        }),
        gridColumns.withTooltip({
            field: 'terminationDate',
            headerName: t('AnelmaContract:1011'),
            width: 110,
            sortable: false,
            valueGetter: (params, row) => {
                const d = (row as IContractHistoryRow).terminationDate;
                return fmt(d);
            },
        }),
    ];

    const getDetailedPanelContent = ({ row }: GridRowParams) => {
        let contract = row as IContractListItem;

        return (
            <>
                <Grid style={{ paddingLeft: '24px', paddingBottom: '16px', paddingTop: '16px' }}>
                    <Typography variant='h5'>{t('AnelmaContract:1009')}</Typography>
                </Grid>

                <Grid style={{ paddingLeft: '50px', marginBottom: '20px', maxWidth: '1000px' }}>
                    <ADataGrid
                        disableSelectionOnClick={true}
                        rows={getDetailedPanelRows(contract)}
                        columns={detailedPanelColumns}
                    />
                </Grid>
            </>
        );
    };

    // Helper to get the EffectiveTo value as a raw Date (or null) for proper date column parsing
    const getEffectiveToDate = (contract: IContractListItem): Date | null => {
        if (contract.TerminationDate) {
            const notice = getNoticePeriod(contract);
            const months = getNoticeMonths(notice, !!contract.TerminatedBySnellman);

            // Calculate the adjusted termination effective date and return as Date or null
            const terminationDate = utils.date.utcStringToLocalMoment(
                contract.TerminationDate.toString()
            );
            return terminationDate ? terminationDate.add(months, 'months').toDate() : null;
        }
        if (contract.EffectiveTo) {
            const effectiveTo = utils.date.utcStringToLocalMoment(contract.EffectiveTo.toString());
            return effectiveTo ? effectiveTo.toDate() : null;
        }
        return null;
    };

    const contractDataRows = contracts.map((c, i) => ({
        id: i,
        ...c,
        EffectiveFrom: utils.date.utcStringToLocalDate(c.EffectiveFrom.toString()),
        EffectiveToDisplay: getEffectiveToDate(c),
    }));

    return loading ? (
        <ViewLoader />
    ) : (
        <>
            {contractDataRows && (
                <ADataGrid
                    apiRef={apiRef}
                    disableSelectionOnClick={true}
                    rows={contractDataRows}
                    columns={columns}
                    actions={getActions()}
                    getDetailedPanelContent={getDetailedPanelContent}
                    enableFiltering
                    enablePagination
                    //enableRowNumbers
                />
            )}
        </>
    );
}
